"use client";

import { useEffect, useState } from "react";
import {
  limit as fbLimit,
  onSnapshot,
  orderBy,
  query,
  where,
} from "firebase/firestore";
import { getDb } from "@/lib/firebase/client";
import { paths, refCol } from "@/lib/db/paths";
import { transactionSchema, type Transaction } from "@/lib/schemas";

type State = {
  transactions: Transaction[];
  loading: boolean;
  error: string | null;
};

const cache = new Map<string, Transaction[]>();
const cacheKey = (hid: string, uid: string, max: number) =>
  `${hid}|${uid}|${max}`;

export function useTransactions(
  householdId: string | undefined,
  userId: string | undefined,
  max = 50,
): State {
  const key =
    householdId && userId ? cacheKey(householdId, userId, max) : "";
  const cached = key ? cache.get(key) : undefined;
  const [state, setState] = useState<State>({
    transactions: cached ?? [],
    loading: cached === undefined,
    error: null,
  });

  useEffect(() => {
    if (!householdId || !userId) return;
    const db = getDb();
    const q = query(
      refCol(db, paths.transactions(householdId)),
      where("userId", "==", userId),
      orderBy("createdAt", "desc"),
      fbLimit(max),
    );
    const k = cacheKey(householdId, userId, max);
    const unsub = onSnapshot(
      q,
      (snap) => {
        const transactions = snap.docs.flatMap((d) => {
          const parsed = transactionSchema.safeParse({
            id: d.id,
            ...d.data(),
          });
          return parsed.success ? [parsed.data] : [];
        });
        cache.set(k, transactions);
        setState({ transactions, loading: false, error: null });
      },
      (err) =>
        setState({ transactions: [], loading: false, error: err.message }),
    );
    return () => unsub();
  }, [householdId, userId, max]);

  return state;
}
